"use client";

import { BreadCrumbs } from "../BreadCrumbs";
import { FAQ, Spacer, Text } from "@zapier/design-system";

import {
  ProductCard,
  RelatedContent,
  RelatedTemplates,
  Markdown,
  FAQSchema,
} from "./components";
import { Container } from "src/components/Container";
import styles from "./TemplateLibraryDetail.module.css";
import { CrossDomainImage } from "./components/CrossDomainImage";
import { ZapCardTemplate } from "../ZapCard/ZapCard";
import { MetaProduct } from "src/server/zsl/templates/types";
import { TemplateLibraryDetailHeader } from "./components/TemplateLibraryDetailHeader";
import { TemplateLibraryDetailCTAs } from "./components/TemplateLibraryDetailCTAs";
import { useFeatureFlag } from "src/components/FeatureFlags";
import { useSearchParams } from "next/navigation";
import { TemplateDetailPage } from "../TemplateDetailPage/TemplateDetailPage";
import { getBreadcrumbs } from "../TemplateDetailPage/constants";

interface TemplateLibraryDetailProps {
  template: ZapCardTemplate;
  relatedTemplates: ZapCardTemplate[];
  isDraftModeEnabled: boolean;
  isLoggedIn: boolean;
}

export function TemplateLibraryDetail({
  template,
  isDraftModeEnabled,
  relatedTemplates,
  isLoggedIn,
}: TemplateLibraryDetailProps) {
  // Determine whether to show the unified details page
  const isUnifiedDetailsPageEnabled = useFeatureFlag(
    "templates_unified_details_page",
  );
  const searchParams = useSearchParams();
  const showUnifiedDetailsPage =
    isUnifiedDetailsPageEnabled ||
    (!isLoggedIn && searchParams?.get("showUnifiedDetailsPage") === "true"); // Workaround for testing in logged-out pages

  return showUnifiedDetailsPage ? (
    <TemplateDetailPage
      template={template}
      relatedTemplates={relatedTemplates}
      isDraftModeEnabled={isDraftModeEnabled}
      isLoggedIn={isLoggedIn}
    />
  ) : (
    <>
      <Container>
        <FAQSchema questions={template.faqs?.items ?? null} />
        <div className={styles.breadCrumbWrapper}>
          <BreadCrumbs crumbs={getBreadcrumbs(template)} />
        </div>
        <div className={styles.heroContent}>
          <TemplateLibraryDetailHeader
            template={template}
            isDraftModeEnabled={isDraftModeEnabled}
          />
          {template.previewImage ? (
            <div className={styles.imageWrapper}>
              <CrossDomainImage
                className={styles.image}
                src={template.previewImage.url}
                alt={template.previewImage.title}
                fill
                style={{
                  objectFit: "cover",
                  objectPosition: "center top",
                }}
              />
            </div>
          ) : null}
        </div>
        <div className={styles.content}>
          <div className={styles.main}>
            <div>
              {template.content ? (
                <Markdown content={template.content} />
              ) : null}
            </div>
            <div className={styles.sidebar}>
              <Text type="SectionHeader">Made with</Text>
              <div className={styles.products}>
                {template.products?.items.map((product) => (
                  <ProductCard
                    key={product.slug}
                    product={product.slug as MetaProduct}
                  />
                ))}
              </div>
            </div>
          </div>
          {template.faqs?.items ? (
            <span className={styles.faq}>
              <FAQ
                items={template.faqs?.items.map((i) => ({
                  ...i,
                  content: <Markdown content={i.content} />,
                }))}
              />
            </span>
          ) : null}
          <section className={styles.ctaSection}>
            <Text tag="h2" type="PageHeader">
              {template.name}
            </Text>
            <div className={styles.ctaSectionButtonsContainer}>
              <TemplateLibraryDetailCTAs
                template={template}
                buttonSize="large"
              />
            </div>
          </section>
          <RelatedTemplates relatedTemplates={relatedTemplates} />
          <Spacer height={80} />
          <RelatedContent template={template} />
        </div>
      </Container>
    </>
  );
}
